import { SavedViewsDropdown } from '@/features/views/components/SavedViewsDropdown';
import { CreateWorkItemModal } from '@/features/work-items/components/CreateWorkItemModal';
import { ListView } from '@/features/work-items/views/ListView';
import { ViewSwitcher } from '@/features/work-items/views/ViewSwitcher';
import { useQuickCreate } from '@/lib/hooks/useQuickCreate';
import { getProjectBySlugQuery } from '@/lib/supabase/queries/projects';
import { getWorkspaceBySlugQuery } from '@/lib/supabase/queries/workspaces';
import { useQuery } from '@tanstack/react-query';
import { Link, createFileRoute, useNavigate, useParams } from '@tanstack/react-router';
import { Settings } from 'lucide-react';
import { useState } from 'react';

export const Route = createFileRoute('/_app/$wsSlug/projects/$projectSlug/list')({
  component: ListPage,
});

function ListPage() {
  const { wsSlug, projectSlug } = useParams({
    from: '/_app/$wsSlug/projects/$projectSlug/list',
  });
  const navigate = useNavigate();

  const wsQ = useQuery(getWorkspaceBySlugQuery(wsSlug));
  const projQ = useQuery({
    ...getProjectBySlugQuery(wsQ.data?.id ?? '', projectSlug),
    enabled: !!wsQ.data?.id,
  });

  const [createOpen, setCreateOpen] = useState(false);
  useQuickCreate(() => setCreateOpen(true));

  if (wsQ.isPending || (wsQ.data && projQ.isPending)) {
    return <div className="p-6 text-sm text-quiet">Cargando proyecto...</div>;
  }
  if (!wsQ.data || !projQ.data) {
    return (
      <div className="p-6 text-sm text-quiet">
        Proyecto no encontrado o sin permisos para verlo.
      </div>
    );
  }
  const ws = wsQ.data;
  const project = projQ.data;

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center justify-between px-3 pt-3 pb-2 border-b border-border-soft">
        <div>
          <h2 className="text-lg font-semibold text-ink">{project.name}</h2>
          <p className="text-sm text-quiet mt-0.5">
            Vista de lista con todas las tareas del proyecto. Útil para revisar grandes volúmenes,
            ordenar y filtrar.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <SavedViewsDropdown projectId={project.id} workspaceId={ws.id} currentLayout="list" />
          <ViewSwitcher
            value="list"
            onChange={(next) => {
              if (next === 'kanban') {
                navigate({
                  to: '/$wsSlug/projects/$projectSlug/board',
                  params: { wsSlug, projectSlug },
                });
              } else if (next === 'gantt') {
                navigate({
                  to: '/$wsSlug/projects/$projectSlug/gantt',
                  params: { wsSlug, projectSlug },
                });
              }
            }}
          />
          <Link
            to="/$wsSlug/projects/$projectSlug/settings"
            params={{ wsSlug, projectSlug }}
            className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-xs text-quiet hover:text-ink hover:bg-accent-sf"
            title="Configuracion del proyecto"
          >
            <Settings size={14} />
          </Link>
        </div>
      </div>
      <div className="flex-1 min-h-0">
        <ListView
          projectId={project.id}
          projectIdentifier={project.identifier}
          onOpenItem={(id) =>
            navigate({
              to: '/$wsSlug/projects/$projectSlug/$itemId',
              params: { wsSlug, projectSlug, itemId: id },
            })
          }
        />
      </div>
      <CreateWorkItemModal
        open={createOpen}
        onOpenChange={setCreateOpen}
        workspaceId={ws.id}
        projectId={project.id}
      />
    </div>
  );
}
